import { useCallback, useEffect, useRef, useState } from 'react';
import { copyToClipboard } from '../services';

/** How long the "コピーしました" success state stays visible before resetting. */
export const COPY_FEEDBACK_RESET_MS = 1600;

export interface CopyFeedback {
  canCopy: boolean;
  isCopied: boolean;
  copyLabel: string;
  handleCopy: () => Promise<void>;
}

/**
 * Encapsulates copy-to-clipboard feedback for assistant message content:
 * the copied flag, its auto-reset timer, unmount cleanup, and the label.
 */
export function useCopyFeedback(content?: string): CopyFeedback {
  const [isCopied, setIsCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
    };
  }, []);

  const canCopy = (content?.trim().length ?? 0) > 0;

  const handleCopy = useCallback(async () => {
    if (!canCopy) {
      return;
    }
    const didCopy = await copyToClipboard(content ?? '');
    if (!didCopy) {
      return;
    }
    setIsCopied(true);
    if (resetTimer.current) {
      clearTimeout(resetTimer.current);
    }
    resetTimer.current = setTimeout(() => setIsCopied(false), COPY_FEEDBACK_RESET_MS);
  }, [canCopy, content]);

  return {
    canCopy,
    isCopied,
    copyLabel: isCopied ? 'コピーしました' : 'コピー',
    handleCopy,
  };
}
